import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { adminApi, rememberAdminAccessToken } from '@/lib/admin-api';
import { adminUrl, supabase, supabaseAnonKey, supabaseUrl } from '@/lib/supabase';
import type { StaffSession } from '@/types';

type SessionContextValue = {
  authSession: Session | null;
  staffSession: StaffSession | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  signIn: (provider: 'google' | 'apple') => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);
const DIRECT_ADMIN_NAMES = new Map([
  ['d41f2438-9435-4963-84d8-818402e20bfa', 'Hakob Ter-Sahakyan'],
  ['a4c112b6-b535-4d6a-99d3-f9e6a0f41ae0', 'Hakob Ter-Sahakyan'],
  ['c822d39c-a4ed-4477-98a6-e4ce05bf6775', 'Meri Safaryan'],
]);
const DIRECT_ADMIN_EMAILS = new Map([
  ['hakob.ter.sahakyan@gmail.com', 'Hakob Ter-Sahakyan'],
]);

function tokenClaims(accessToken: string) {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
      sub?: string;
      email?: string;
    };
  } catch {
    return null;
  }
}

function directStaffSession(accessToken: string): StaffSession | null {
  const claims = tokenClaims(accessToken);
  const userId = claims?.sub ?? '';
  const email = claims?.email?.toLowerCase() ?? '';
  const displayName = DIRECT_ADMIN_NAMES.get(userId) ?? DIRECT_ADMIN_EMAILS.get(email);
  if (!displayName) return null;
  return {
    user: { id: userId, email: claims?.email ?? null },
    staff: {
      user_id: userId,
      role: 'platform_admin',
      display_name: displayName,
      granted_at: new Date(0).toISOString(),
      revoked_at: null,
    },
  };
}

function callbackParams() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return {
    code: search.get('code'),
    accessToken: hash.get('access_token'),
    refreshToken: hash.get('refresh_token'),
    error: search.get('error_description') || hash.get('error_description'),
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [authSession, setAuthSession] = useState<Session | null>(null);
  const [staffSession, setStaffSession] = useState<StaffSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applySession = useCallback(async (session: Session | null) => {
    setAuthSession(session);
    if (!session) {
      rememberAdminAccessToken(null);
      setStaffSession(null);
      setLoading(false);
      return;
    }

    rememberAdminAccessToken(session.access_token);
    const directSession = directStaffSession(session.access_token);
    if (directSession) {
      setStaffSession(directSession);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setStaffSession(await adminApi.session(session.access_token));
      setError(null);
    } catch (nextError) {
      setStaffSession(null);
      setError(nextError instanceof Error ? nextError.message : 'Unable to verify staff access');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const { code, accessToken, refreshToken, error: callbackError } = callbackParams();

    const fail = (message: string) => {
      if (!active) return;
      setError(message);
      setLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active || event === 'INITIAL_SESSION') return;
      if (session) {
        void applySession(session);
        return;
      }
      if (event === 'SIGNED_OUT' && !code && !accessToken) {
        void applySession(null);
      }
    });

    void (async () => {
      if (callbackError) {
        fail(callbackError);
        return;
      }

      if (code) {
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (!active) return;
        if (exchangeError) {
          fail(exchangeError.message);
          return;
        }
        await applySession(data.session);
        return;
      }

      if (accessToken && refreshToken) {
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (!active) return;
        if (sessionError) {
          fail(sessionError.message);
          return;
        }
        window.history.replaceState(null, '', window.location.pathname);
        await applySession(data.session);
        return;
      }

      const { data, error: sessionError } = await supabase.auth.getSession();
      if (!active) return;
      if (sessionError) {
        fail(sessionError.message);
        return;
      }
      await applySession(data.session);
    })();

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const refresh = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    await applySession(session);
  }, [applySession]);

  const signIn = useCallback(async (provider: 'google' | 'apple') => {
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${adminUrl}/auth/callback` },
    });
    if (signInError) throw signInError;
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    rememberAdminAccessToken(null);
    await supabase.auth.signOut({ scope: 'local' });
    const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.trim(), password }),
    });
    const payload = await response.json().catch(() => null) as {
      access_token?: string;
      refresh_token?: string;
      error_description?: string;
      msg?: string;
      message?: string;
      error?: string;
    } | null;
    if (!response.ok || !payload?.access_token || !payload.refresh_token) {
      throw new Error(payload?.error_description ?? payload?.msg ?? payload?.message ?? payload?.error ?? 'Unable to sign in');
    }
    const { data, error: sessionError } = await supabase.auth.setSession({
      access_token: payload.access_token,
      refresh_token: payload.refresh_token,
    });
    if (sessionError) throw sessionError;
    await applySession(data.session);
  }, [applySession]);

  const signOut = useCallback(async () => {
    rememberAdminAccessToken(null);
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) throw signOutError;
    setAuthSession(null);
    setStaffSession(null);
    setError(null);
    setLoading(false);
  }, []);

  const value = useMemo(
    () => ({ authSession, staffSession, loading, error, refresh, signIn, signInWithPassword, signOut }),
    [authSession, staffSession, loading, error, refresh, signIn, signInWithPassword, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
